import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { apiError, money, CURRENCIES, convertCurrency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ImageUploader } from "@/components/ImageUploader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Loader2, Search, PackagePlus, Save, ChevronsUpDown, AlertTriangle } from "lucide-react";

const newItem = () => ({ product_id: null, name: "", stock_code: "", description: "", note: "", image_url: "", quantity: 1, unit_price: 0, discount_percent: 0, cost_price: 0, below_cost_approved: false });

export default function QuoteEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isEdit = Boolean(id);
  const { user, isAdmin, can } = useAuth();

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prodOpen, setProdOpen] = useState(false);
  const [custOpen, setCustOpen] = useState(false);
  const [confirmCost, setConfirmCost] = useState(null);
  const [prodQuery, setProdQuery] = useState("");
  const [custQuery, setCustQuery] = useState("");

  const [mode, setMode] = useState("registered");
  const [customerId, setCustomerId] = useState("");
  const [adhoc, setAdhoc] = useState({ name: "", phone: "", email: "" });
  const [items, setItems] = useState([newItem()]);
  const [currency, setCurrency] = useState("TRY");
  const [vatEnabled, setVatEnabled] = useState(true);
  const [vatRate, setVatRate] = useState(20);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState("");
  const [preparedBy, setPreparedBy] = useState("");
  const [rates, setRates] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.get("/settings");
      setRates(s.data.exchange_rates || {});
      const qd = s.data.quote_defaults || {};
      if (isEdit) {
        const { data } = await api.get(`/quotes/${id}`);
        const owner = data.prepared_by === user?.id;
        if (!(isAdmin || owner || can("quote_edit_any"))) {
          toast.error("Bu teklifi düzenleme yetkiniz yok");
          navigate(`/teklifler/${id}`);
          return;
        }
        setMode(data.customer_id ? "registered" : "adhoc");
        setCustomerId(data.customer_id || "");
        setAdhoc({ name: data.customer_snapshot?.name || data.ad_hoc_name || "", phone: data.customer_snapshot?.phone || "", email: data.customer_snapshot?.email || "" });
        setItems(data.items?.length ? data.items : [newItem()]);
        setCurrency(data.currency || "TRY");
        setVatEnabled(data.vat_rate !== null && data.vat_rate !== undefined);
        setVatRate(data.vat_rate ?? 20);
        setTotalDiscount(data.total_discount_percent || 0);
        setValidUntil(data.valid_until || "");
        setNotes(data.notes || ""); setTerms(data.terms || "");
        setPreparedBy(data.prepared_by_name || "");
        if (data.customer_id) {
          try { const c = await api.get(`/customers/${data.customer_id}`); setSelectedCustomer(c.data); } catch { /* ignore */ }
        }
      } else {
        const preselect = params.get("customer");
        if (preselect) {
          setMode("registered"); setCustomerId(preselect);
          try { const c = await api.get(`/customers/${preselect}`); setSelectedCustomer(c.data); } catch { /* ignore */ }
        }
        setVatRate(qd.default_vat_rate ?? 20);
        setCurrency(qd.currency || "TRY");
        setTerms(qd.terms_tr || "");
        if (qd.validity_days) {
          const d = new Date(); d.setDate(d.getDate() + qd.validity_days);
          setValidUntil(d.toISOString().slice(0, 10));
        }
      }
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  }, [id, isEdit, params, user, isAdmin, can, navigate]);

  useEffect(() => { load(); }, [load]);

  // Server-side search for the product picker (avoids loading the whole catalog)
  useEffect(() => {
    if (!prodOpen) return;
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/products", { params: { q: prodQuery, limit: 30 } }); setProducts(data); } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [prodQuery, prodOpen]);

  // Server-side search for the customer picker
  useEffect(() => {
    if (!custOpen) return;
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/customers", { params: { q: custQuery, limit: 30 } }); setCustomers(data); } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [custQuery, custOpen]);

  const updateItem = (idx, patch) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const addManual = () => setItems((arr) => [...arr, newItem()]);
  const addProduct = (p) => {
    const from = p.currency || "TRY";
    const price = Number(p.price) || 0;
    const costN = Number(p.cost_price) || 0;
    let u = convertCurrency(price, from, currency, rates);
    let c = convertCurrency(costN, from, currency, rates);
    const missing = u === null || (costN > 0 && c === null);
    if (u === null) u = price;
    if (c === null) c = costN;
    u = Math.round(u * 100) / 100; c = Math.round(c * 100) / 100;
    const item = { ...newItem(), product_id: p.id, name: p.name, stock_code: p.stock_code, description: p.description, image_url: p.image_url, unit_price: u, cost_price: c };
    setItems((arr) => {
      const base = arr.length === 1 && !arr[0].name ? [] : arr;
      const next = [...base, item];
      if (c > 0 && u < c) setConfirmCost({ idx: next.length - 1, cost: c, price: u });
      return next;
    });
    setProdOpen(false);
    if (missing && from !== currency) toast.warning(`${from} kuru tanımlı değil — fiyat çevrilemedi. Ayarlar > Teklif'ten kuru girin.`);
    else toast.success(`${p.name} eklendi`);
  };

  const changeCurrency = (newCur) => {
    if (newCur === currency) return;
    let missing = false;
    setItems((arr) => arr.map((it) => {
      const u = convertCurrency(it.unit_price, currency, newCur, rates);
      const c = convertCurrency(it.cost_price, currency, newCur, rates);
      if (u === null || c === null) missing = true;
      return { ...it, unit_price: u === null ? it.unit_price : Math.round(u * 100) / 100, cost_price: c === null ? it.cost_price : Math.round(c * 100) / 100 };
    }));
    setCurrency(newCur);
    if (missing) toast.warning("Bazı kurlar tanımlı değil — ilgili fiyatlar çevrilemedi. Ayarlar > Teklif'ten kur girin.");
  };

  const checkCost = (idx) => {
    const it = items[idx];
    if (!it) return;
    const cost = Number(it.cost_price) || 0;
    const up = Number(it.unit_price) || 0;
    if (cost > 0 && up < cost && !it.below_cost_approved) setConfirmCost({ idx, cost, price: up });
  };
  const approveConfirm = () => {
    if (confirmCost) updateItem(confirmCost.idx, { below_cost_approved: true });
    setConfirmCost(null);
  };
  const cancelConfirm = () => {
    if (confirmCost) updateItem(confirmCost.idx, { unit_price: confirmCost.cost, below_cost_approved: false });
    setConfirmCost(null);
  };

  const totals = useMemo(() => {
    let net = 0;
    items.forEach((it) => { net += (Number(it.quantity) || 0) * (Number(it.unit_price) || 0) * (1 - (Number(it.discount_percent) || 0) / 100); });
    const vatAmt = vatEnabled ? net * (Number(vatRate) || 0) / 100 : 0;
    const gross = net + vatAmt;
    const totalDiscAmt = gross * (Number(totalDiscount) || 0) / 100;
    const grand = gross - totalDiscAmt;
    return { net, vatAmt, gross, totalDiscAmt, grand };
  }, [items, totalDiscount, vatEnabled, vatRate]);

  const save = async () => {
    const valid = items.filter((it) => it.name.trim());
    if (valid.length === 0) { toast.error("En az bir ürün ekleyin"); return; }
    if (mode === "registered" && !customerId) { toast.error("Müşteri seçin"); return; }
    if (mode === "adhoc" && !adhoc.name.trim()) { toast.error("Müşteri adı girin"); return; }
    const blocked = valid.find((it) => Number(it.cost_price) > 0 && Number(it.unit_price) < Number(it.cost_price) && !it.below_cost_approved);
    if (blocked) { toast.error(`"${blocked.name}" alış fiyatının altında — lütfen onaylayın`); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: mode === "registered" ? customerId : null,
        ad_hoc_name: mode === "adhoc" ? adhoc.name : "",
        customer_snapshot: mode === "adhoc" ? { name: adhoc.name, phone: adhoc.phone, email: adhoc.email } : {},
        items: valid.map((it) => ({
          product_id: it.product_id, name: it.name, stock_code: it.stock_code || "",
          description: it.description || "", note: it.note || "", image_url: it.image_url || "",
          quantity: Number(it.quantity) || 0, unit_price: Number(it.unit_price) || 0,
          discount_percent: Number(it.discount_percent) || 0,
          cost_price: Number(it.cost_price) || 0, below_cost_approved: !!it.below_cost_approved,
        })),
        currency, vat_rate: vatEnabled ? Number(vatRate) || 0 : null,
        total_discount_percent: Number(totalDiscount) || 0,
        valid_until: validUntil, notes, terms, status: "draft",
      };
      const res = isEdit ? await api.put(`/quotes/${id}`, payload) : await api.post("/quotes", payload);
      toast.success(isEdit ? "Teklif revize edildi" : "Teklif oluşturuldu");
      navigate(`/teklifler/${res.data.id}`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-600" /></div>;

  const SaveBtn = ({ testid }) => (
    <Button className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl" onClick={save} disabled={saving} data-testid={testid}>
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />{isEdit ? "Revize Et" : "Kaydet"}</>}
    </Button>
  );

  return (
    <div>
      <Button variant="ghost" className="mb-4 -ml-2 rounded-xl" onClick={() => navigate(-1)} data-testid="qe-back"><ArrowLeft className="w-4 h-4 mr-2" /> Geri</Button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{isEdit ? "Teklifi Düzenle" : "Yeni Teklif"}</h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="qe-prepared-by">Hazırlayan: <span className="font-medium text-slate-700">{(isEdit ? preparedBy : user?.name) || "-"}</span></p>
        </div>
        <SaveBtn testid="qe-save" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Customer */}
          <Card className="p-6 border-slate-100 rounded-2xl card-soft">
            <h3 className="font-heading font-bold mb-4">Müşteri</h3>
            <div className="flex gap-2 mb-4">
              <Button type="button" variant={mode === "registered" ? "default" : "outline"} size="sm" className={`rounded-xl ${mode === "registered" ? "bg-slate-900" : ""}`} onClick={() => setMode("registered")} data-testid="qe-mode-registered">Kayıtlı Müşteri</Button>
              <Button type="button" variant={mode === "adhoc" ? "default" : "outline"} size="sm" className={`rounded-xl ${mode === "adhoc" ? "bg-slate-900" : ""}`} onClick={() => setMode("adhoc")} data-testid="qe-mode-adhoc">Hızlı (Kayıtsız)</Button>
            </div>
            {mode === "registered" ? (
              <Popover open={custOpen} onOpenChange={setCustOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between rounded-xl border-slate-200 font-normal h-11" data-testid="qe-customer-select">
                    <span className="truncate">{selectedCustomer ? (selectedCustomer.company_name || selectedCustomer.authorized_name) : "Müşteri seçin veya arayın..."}</span>
                    <ChevronsUpDown className="w-4 h-4 opacity-50 ml-2 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput placeholder="İsim, vergi no, telefon ara..." value={custQuery} onValueChange={setCustQuery} data-testid="qe-customer-search" />
                    <CommandList>
                      <CommandEmpty>Müşteri bulunamadı.</CommandEmpty>
                      <CommandGroup>
                        {customers.map((c) => (
                          <CommandItem key={c.id} value={c.id}
                            onSelect={() => { setCustomerId(c.id); setSelectedCustomer(c); setCustOpen(false); }} data-testid={`qe-customer-opt-${c.id}`}>
                            <div className="flex flex-col">
                              <span className="font-medium">{c.company_name || c.authorized_name}</span>
                              {c.company_name && c.authorized_name && <span className="text-xs text-slate-400">{c.authorized_name}</span>}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              <div className="grid sm:grid-cols-3 gap-3">
                <Input placeholder="Ad Soyad *" value={adhoc.name} onChange={(e) => setAdhoc((a) => ({ ...a, name: e.target.value }))} data-testid="qe-adhoc-name" />
                <Input placeholder="Telefon" value={adhoc.phone} onChange={(e) => setAdhoc((a) => ({ ...a, phone: e.target.value }))} data-testid="qe-adhoc-phone" />
                <Input placeholder="E-posta" value={adhoc.email} onChange={(e) => setAdhoc((a) => ({ ...a, email: e.target.value }))} data-testid="qe-adhoc-email" />
              </div>
            )}
          </Card>

          {/* Items */}
          <Card className="p-6 border-slate-100 rounded-2xl card-soft">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h3 className="font-heading font-bold">Ürünler</h3>
              <div className="flex gap-2">
                <Popover open={prodOpen} onOpenChange={setProdOpen}>
                  <PopoverTrigger asChild>
                    <Button size="sm" variant="outline" className="rounded-xl" data-testid="qe-add-product"><Search className="w-4 h-4 mr-2" />Sistemden Ekle</Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-80" align="end">
                    <Command shouldFilter={false}>
                      <CommandInput placeholder="Ürün ara..." value={prodQuery} onValueChange={setProdQuery} />
                      <CommandList>
                        <CommandEmpty>Ürün bulunamadı.</CommandEmpty>
                        <CommandGroup>
                          {products.map((p) => (
                            <CommandItem key={p.id} value={p.id} onSelect={() => addProduct(p)} data-testid={`qe-prod-opt-${p.id}`}>
                              <div className="flex justify-between w-full">
                                <span className="truncate">{p.name}</span>
                                <span className="text-slate-400 text-xs ml-2">{money(p.price, p.currency)}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <Button size="sm" className="bg-slate-900 rounded-xl" onClick={addManual} data-testid="qe-add-manual"><PackagePlus className="w-4 h-4 mr-2" />Manuel Ekle</Button>
              </div>
            </div>

            <div className="space-y-4">
              {items.map((it, idx) => (
                <div key={idx} className="border border-slate-100 rounded-xl p-4 bg-slate-50/50" data-testid={`qe-item-${idx}`}>
                  <div className="flex items-start gap-2 mb-3">
                    <Input placeholder="Ürün ismi" value={it.name} onChange={(e) => updateItem(idx, { name: e.target.value })} className="font-medium bg-white" data-testid={`qe-item-name-${idx}`} />
                    <Input placeholder="Stok Kodu" value={it.stock_code} onChange={(e) => updateItem(idx, { stock_code: e.target.value })} className="w-32 hidden sm:block bg-white" />
                    <Button variant="ghost" size="icon" className="text-rose-600 shrink-0" onClick={() => removeItem(idx)} data-testid={`qe-item-remove-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  <div className="mb-3">
                    <Label className="text-xs mb-1.5 block">Ürün Görseli</Label>
                    <ImageUploader value={it.image_url} onChange={(url) => updateItem(idx, { image_url: url })} />
                  </div>
                  <Textarea placeholder="Ürün açıklaması" value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value })} rows={2} className="mb-2 text-sm bg-white" />
                  <Textarea placeholder="Özel açıklamalar — her satıra bir madde yazın (ürün özellikleri gibi)" value={it.note} onChange={(e) => updateItem(idx, { note: e.target.value })} rows={3} className="mb-1 text-sm text-rose-700 bg-white" data-testid={`qe-item-note-${idx}`} />
                  <p className="text-[11px] text-slate-400 mb-3">Her satır teklifte ayrı bir madde (•) olarak görünür.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-start">
                    <div><Label className="text-xs">Miktar</Label><Input type="number" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} className="bg-white" data-testid={`qe-item-qty-${idx}`} /></div>
                    <div><Label className="text-xs">Birim Fiyat</Label><Input type="number" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: e.target.value, below_cost_approved: false })} onBlur={() => checkCost(idx)} className={`bg-white ${Number(it.cost_price) > 0 && Number(it.unit_price) < Number(it.cost_price) ? "border-amber-400" : ""}`} data-testid={`qe-item-price-${idx}`} />
                      {Number(it.cost_price) > 0 && (
                        <p className={`text-[11px] mt-1 ${Number(it.unit_price) < Number(it.cost_price) ? (it.below_cost_approved ? "text-emerald-600" : "text-amber-600 font-medium") : "text-slate-400"}`} data-testid={`qe-item-cost-${idx}`}>
                          Alış: {money(it.cost_price, currency)}{Number(it.unit_price) < Number(it.cost_price) && (it.below_cost_approved ? " · Onaylandı" : " · Altında!")}
                        </p>
                      )}
                    </div>
                    <div><Label className="text-xs">İskonto %</Label><Input type="number" value={it.discount_percent} onChange={(e) => updateItem(idx, { discount_percent: e.target.value })} className="bg-white" data-testid={`qe-item-disc-${idx}`} /></div>
                    <div className="text-right"><Label className="text-xs">Tutar</Label><p className="font-heading font-bold text-lg mt-1">{money((Number(it.quantity) || 0) * (Number(it.unit_price) || 0) * (1 - (Number(it.discount_percent) || 0) / 100), currency)}</p></div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6 border-slate-100 rounded-2xl card-soft space-y-4">
            <h3 className="font-heading font-bold">Notlar & Şartlar</h3>
            <div className="space-y-2"><Label>Notlar</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="qe-notes" /></div>
            <div className="space-y-2"><Label>Şartlar</Label><Textarea value={terms} onChange={(e) => setTerms(e.target.value)} rows={2} data-testid="qe-terms" /></div>
          </Card>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <Card className="p-6 border-slate-100 rounded-2xl card-soft lg:sticky lg:top-6">
            <h3 className="font-heading font-bold mb-4">Özet</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Para Birimi</Label>
                  <Select value={currency} onValueChange={changeCurrency}>
                    <SelectTrigger data-testid="qe-currency"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="TRY">₺ TL</SelectItem><SelectItem value="EUR">€ EUR</SelectItem><SelectItem value="USD">$ USD</SelectItem></SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-400">1$ = {rates.usd ? money(rates.usd, "TRY") : "—"} · 1€ = {rates.eur ? money(rates.eur, "TRY") : "—"}</p>
                </div>
                <div className="space-y-1"><Label className="text-xs">Geçerlilik</Label><Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="qe-valid" /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Genel İskonto (%)</Label><Input type="number" value={totalDiscount} onChange={(e) => setTotalDiscount(e.target.value)} data-testid="qe-total-disc" /></div>

              <div className="flex items-center justify-between py-2 border-y border-slate-100">
                <div><Label className="text-sm">KDV Uygula</Label><p className="text-xs text-slate-400">Kapalıysa "KDV dahildir" yazılır</p></div>
                <Switch checked={vatEnabled} onCheckedChange={setVatEnabled} data-testid="qe-vat-switch" />
              </div>
              {vatEnabled && <div className="space-y-1"><Label className="text-xs">KDV Oranı (%)</Label><Input type="number" value={vatRate} onChange={(e) => setVatRate(e.target.value)} data-testid="qe-vat-rate" /></div>}

              <div className="pt-3 space-y-2 text-sm">
                {vatEnabled ? (
                  <>
                    <div className="flex justify-between"><span className="text-slate-500">Ara Toplam (KDV Hariç)</span><span>{money(totals.net, currency)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">KDV (%{vatRate})</span><span>{money(totals.vatAmt, currency)}</span></div>
                    <div className="flex justify-between font-medium"><span className="text-slate-600">Ara Toplam (KDV Dahil)</span><span>{money(totals.gross, currency)}</span></div>
                    {totals.totalDiscAmt > 0 && <div className="flex justify-between text-rose-600"><span>Genel İskonto (KDV dahil)</span><span>- {money(totals.totalDiscAmt, currency)}</span></div>}
                  </>
                ) : (
                  <>
                    <div className="flex justify-between"><span className="text-slate-500">Ara Toplam</span><span>{money(totals.net, currency)}</span></div>
                    {totals.totalDiscAmt > 0 && <div className="flex justify-between text-rose-600"><span>Genel İskonto</span><span>- {money(totals.totalDiscAmt, currency)}</span></div>}
                    <div className="text-xs text-slate-400 italic">Fiyatlara KDV dahildir.</div>
                  </>
                )}
                <div className="flex justify-between items-center bg-slate-900 text-white rounded-xl px-4 py-3 mt-2">
                  <span className="font-bold">GENEL TOPLAM</span>
                  <span className="font-heading font-black text-lg" data-testid="qe-grand-total">{money(totals.grand, currency)}</span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div className="mt-6 flex justify-end border-t border-slate-200 pt-6">
        <Button size="lg" className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl h-12 px-8" onClick={save} disabled={saving} data-testid="qe-save-bottom">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5 mr-2" />{isEdit ? "Revize Et" : "Teklifi Kaydet"}</>}
        </Button>
      </div>

      <Dialog open={!!confirmCost} onOpenChange={(o) => { if (!o) cancelConfirm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2 text-amber-700"><AlertTriangle className="w-5 h-5" />Alış Fiyatının Altında</DialogTitle></DialogHeader>
          {confirmCost && (
            <div className="py-2 text-sm text-slate-600 space-y-2">
              <p>Girdiğiniz birim fiyat <b>{money(confirmCost.price, currency)}</b>, bu ürünün alış fiyatının <b>{money(confirmCost.cost, currency)}</b> altında.</p>
              <p>Ürünü alış fiyatının altında teklife eklemek için onayınız gerekiyor. Onayınız, teklif detayında ve işlem geçmişinde adınızla kaydedilecektir.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={cancelConfirm} data-testid="cost-confirm-cancel">Vazgeç</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl" onClick={approveConfirm} data-testid="cost-confirm-approve">Onaylıyorum</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
